import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Etiqueta } from '@/components/ui/etiqueta';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { RESULTADOS, TODOS_LOS_DISPAROS } from '../../lib/corridas';
import { CUALQUIER_FECHA, diaDeBolivia, rangoDelAtajo } from '../../lib/rango-de-fechas';
import type { EventoDeLaLinea } from '../../types/corridas.types';
import type { DestinoHoy, FiltroBandeja } from '../../types/price-proposals.types';
import { FiltrosDeCorridas, type FiltroDeCorridas } from './filtros-de-corridas';
import { GraficoDeCorridas } from './grafico-de-corridas';
import { NumerosDelPeriodo } from './numeros-del-periodo';
import { AvisoAntesDelRegistro, LineaDeTiempo } from './linea-de-tiempo';
import { ProductosDeLaCorridaDialog } from './productos-de-la-corrida-dialog';
import { useLineaDeTiempo } from './use-linea-de-tiempo';

/**
 * La vista de una estrategia (spec 275, R2, punto 4; maqueta: `detailHTML`):
 * migas, cabecera con sus datos, los filtros (sin el de estrategia), los
 * números, el gráfico y sus corridas por día.
 */
export function VistaDeLaEstrategia({
  estrategia,
  estado,
  esperan,
  datos,
  acciones,
  resaltar,
  resaltarCuando,
  version = 0,
  puedeCorrer = false,
  puedeReintentar,
  onVolver,
  onIrAPorDecidir,
  onVolverAIntentar,
  onIrA,
  historial,
}: {
  estrategia: { id: string; nombre: string };
  /** «Activa», «Apagada», «Venció», con su tono. */
  estado: { texto: string; tono: 'ok' | 'neutro' };
  esperan: number;
  /** Cómo calcula, A quién alcanza, Permiso, Cada cuánto revisa (los textos de la tarjeta). */
  datos: Array<{ k: string; v: string }>;
  /** «Revisar ahora» y «Más ▾», los mismos de la tarjeta. */
  acciones: ReactNode;
  /** La corrida a resaltar (desde la tira). */
  resaltar?: string | null;
  /** Cuándo fue: si es de antes de los últimos 30 días, el período arranca ese día (si no, no aparecería). */
  resaltarCuando?: string | null;
  version?: number;
  puedeCorrer?: boolean;
  /** Revisión final de rama (F3): si su estrategia puede volver a correr ahora. */
  puedeReintentar?: (estrategiaId: string) => boolean;
  onVolver: () => void;
  onIrAPorDecidir?: () => void;
  onVolverAIntentar?: (estrategiaId: string) => void;
  onIrA?: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
  /** Spec 294 (D-R4-5): «Historial de cambios», al final de la vista. */
  historial?: ReactNode;
}) {
  const hoy = diaDeBolivia();
  const [filtro, setFiltro] = useState<FiltroDeCorridas>(() => {
    const treinta = rangoDelAtajo('30', hoy) ?? CUALQUIER_FECHA;
    // Auditoría (menor): la corrida elegida en la tira tiene que estar en el período.
    const dia = resaltarCuando ? diaDeBolivia(new Date(resaltarCuando)) : null;
    const periodo =
      dia && treinta.desde && dia < treinta.desde ? { desde: dia, hasta: hoy } : treinta;
    return { estrategia: estrategia.id, periodo, disparos: TODOS_LOS_DISPAROS };
  });
  const linea = useLineaDeTiempo(filtro, version);
  const [productosDe, setProductosDe] = useState<EventoDeLaLinea | null>(null);

  // Desde la tira o desde una columna del gráfico: llevar la corrida a la vista
  // y resaltarla un momento. Ronda de arreglos (revisión T10/T11): una vez por
  // pedido; un refresco de la lista no la vuelve a mover.
  const [resaltada, setResaltada] = useState<string | null>(resaltar ?? null);
  const yaLlevada = useRef<string | null>(null);
  const hayLista = !linea.cargando;
  useEffect(() => {
    if (!resaltada) {
      yaLlevada.current = null;
      return;
    }
    if (!hayLista || yaLlevada.current === resaltada) return;
    const tarjeta = document.getElementById(`evento-${resaltada}`);
    if (!tarjeta) return;
    yaLlevada.current = resaltada;
    tarjeta.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [resaltada, hayLista, linea.eventos]);
  useEffect(() => {
    if (!resaltada) return;
    const t = setTimeout(() => setResaltada(null), 2200);
    return () => clearTimeout(t);
  }, [resaltada]);
  const elegirColumna = (e: EventoDeLaLinea) => {
    yaLlevada.current = null;
    setResaltada(e.id);
    if (e.conteos.propuestas > 0) setProductosDe(e);
  };

  // Revisión final de rama (F8): al abrir la vista el foco va a su título (quien
  // usa teclado o lector sigue desde acá, no desde un botón que ya no está).
  const tituloRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    tituloRef.current?.focus();
  }, []);

  return (
    <div className="space-y-3" data-testid="vista-de-la-estrategia">
      <nav aria-label="Migas" className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        <Button variant="link" size="sm" className="h-auto gap-1 p-0" onClick={onVolver}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Volver a estrategias
        </Button>
        <span aria-hidden="true" className="text-muted-foreground">
          ·
        </span>
        <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">
          Estrategias / <b className="text-foreground">{estrategia.nombre}</b>
        </span>
      </nav>

      <section data-caja className="space-y-3 rounded-tarjeta border bg-card shadow-sm p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              ref={tituloRef}
              tabIndex={-1}
              className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-semibold [overflow-wrap:anywhere] focus-visible:outline-none"
            >
              {estrategia.nombre}
              <Etiqueta tono={estado.tono}>{estado.texto}</Etiqueta>
            </h2>
            {esperan > 0 && onIrAPorDecidir ? (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-alerta"
                onClick={onIrAPorDecidir}
              >
                {/* Compite (rediseño del 14-oct): el lugar ahora se llama Revisar precios. */}
                {formatoCantidad(esperan)} {esperan === 1 ? 'espera' : 'esperan'} tu decisión en
                Revisar precios →
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Nada espera tu decisión</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">{acciones}</div>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {datos.map((d) => (
            <div key={d.k} data-caja className="min-w-0">
              <dt className="text-xs text-muted-foreground">{d.k}</dt>
              <dd className="text-sm [overflow-wrap:anywhere]">{d.v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <FiltrosDeCorridas valor={filtro} onCambiar={setFiltro} />

      {linea.cargando ? (
        <Skeleton className="h-64 w-full" />
      ) : linea.error ? (
        <EstadoError onReintentar={linea.reintentar}>{linea.error}</EstadoError>
      ) : (
        <>
          {linea.resumen && <NumerosDelPeriodo resumen={linea.resumen} />}
          <section className="space-y-2 rounded-tarjeta border bg-card shadow-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold">Cómo se comportó fecha a fecha</h3>
                <p className="text-xs text-muted-foreground">
                  Cada columna es una corrida; la altura es cuántas propuestas hizo. «R» marca un
                  recálculo de reglas (sólo la parte de esta estrategia). Tocá una para ver sus
                  productos.
                </p>
              </div>
              <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {RESULTADOS.map((r) => (
                  <li key={r.clave} className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', r.fondo)} />
                    {r.plural}
                  </li>
                ))}
                <li className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className="h-2 w-2 rounded-full bg-baja" />
                  La limitó una regla
                </li>
              </ul>
            </div>
            <GraficoDeCorridas eventos={linea.eventos} onElegir={elegirColumna} />
          </section>
          {linea.eventos.length === 0 ? (
            <p className="rounded-tarjeta border bg-card shadow-sm p-6 text-center text-sm text-muted-foreground">
              No hay corridas con estos filtros.
            </p>
          ) : (
            <LineaDeTiempo
              eventos={linea.eventos}
              hoy={hoy}
              dentroDeLaEstrategia
              resaltada={resaltada}
              puedeCorrer={puedeCorrer}
              puedeReintentar={puedeReintentar}
              onVolverAIntentar={onVolverAIntentar}
              onVerProductos={setProductosDe}
            />
          )}
          {linea.errorMas && <Aviso tono="alerta">{linea.errorMas}</Aviso>}
          {linea.haySiguiente ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={linea.cargandoMas}
                onClick={() => void linea.verMas()}
              >
                Ver más
              </Button>
            </div>
          ) : (
            <AvisoAntesDelRegistro registroDesde={linea.registroDesde} periodo={filtro.periodo} />
          )}
        </>
      )}
      {historial && (
        <section
          className="space-y-2 rounded-tarjeta border bg-card shadow-sm p-4"
          aria-label="Historial de cambios"
        >
          <h3 className="text-sm font-semibold">Historial de cambios</h3>
          {historial}
        </section>
      )}
      <ProductosDeLaCorridaDialog
        evento={productosDe}
        estrategiaDeLaParte={estrategia.id}
        nombreDeLaParte={estrategia.nombre}
        onCerrar={() => setProductosDe(null)}
        onIrA={onIrA}
      />
    </div>
  );
}
